/**
 * Dados do usuário logado guardados no navegador.
 *
 * Isto é apenas um espelho do perfil, para a interface saber quem está na tela sem
 * ir ao servidor a cada render. Quem realmente sustenta a sessão é o cookie de
 * autenticação (HttpOnly), emitido no login: é ele que decide se o usuário continua
 * conectado depois de fechar o navegador, conforme a opção "manter conectado".
 *
 * Por isso este espelho pode ficar velho — o cookie expira sem avisar o navegador.
 * Sempre que a validação de sessão falhar, chame limpaUsuarioDaSessao.
 */

const CHAVE = "user";
const CHAVE_EMULACAO = "emulacao";

export interface UsuarioDaSessao {
  username: string;
  permission: string;
  id: number;
  /** Entrou com a senha padrão e ainda precisa cadastrar uma senha própria. */
  senhaPadrao?: boolean;
}

/**
 * Emulação de perfil/cliente ativa, espelhada do cookie.
 *
 * Assim como o perfil, isto é só uma cópia para a tela: quem manda é o claim dentro
 * do cookie, que é o que a API lê. Serve para a barra já aparecer certa no primeiro
 * render, antes da resposta do servidor.
 */
export interface EmulacaoAtiva {
  emulando: boolean;
  perfilReal?: string | null;
  perfilEmulado?: string | null;
  clienteId?: number | null;
  clienteNome?: string | null;
}

/**
 * Grava o perfil do usuário recém-logado.
 *
 * Usa sempre o localStorage. Antes o destino variava entre localStorage e
 * sessionStorage conforme o "manter conectado", e as telas liam os dois em ordens
 * diferentes — quem logasse duas vezes com usuários diferentes acabava com um
 * registro em cada lugar e telas divergindo sobre qual perfil estava ativo.
 */
export const gravaUsuarioDaSessao = (usuario: UsuarioDaSessao): void => {
  sessionStorage.removeItem(CHAVE);
  localStorage.setItem(CHAVE, JSON.stringify(usuario));
};

/** Perfil do usuário logado, ou null quando não há nada gravado. */
export const leUsuarioDaSessao = (): UsuarioDaSessao | null => {
  const armazenado = localStorage.getItem(CHAVE) ?? sessionStorage.getItem(CHAVE);

  if (!armazenado) return null;

  try {
    return JSON.parse(armazenado) as UsuarioDaSessao;
  } catch {
    return null;
  }
};

/** Emulação ativa, ou null quando o usuário está no próprio perfil. */
export const leEmulacao = (): EmulacaoAtiva | null => {
  const armazenado = localStorage.getItem(CHAVE_EMULACAO);

  if (!armazenado) return null;

  try {
    const emulacao = JSON.parse(armazenado) as EmulacaoAtiva;
    return emulacao?.emulando ? emulacao : null;
  } catch {
    return null;
  }
};

/** Guarda (ou apaga) a emulação ativa. */
export const gravaEmulacao = (emulacao: EmulacaoAtiva | null): void => {
  if (!emulacao?.emulando) {
    localStorage.removeItem(CHAVE_EMULACAO);
    return;
  }

  localStorage.setItem(CHAVE_EMULACAO, JSON.stringify(emulacao));
};

/**
 * Perfil de acesso que vale para a interface.
 *
 * Com emulação ativa devolve o perfil emulado, que é o mesmo que a API usa para
 * autorizar. É o que faz o menu e as rotas protegidas obedecerem à emulação sem
 * precisarem saber que ela existe.
 */
export const lePerfilDoUsuario = (): string | null =>
  leEmulacao()?.perfilEmulado ?? leUsuarioDaSessao()?.permission ?? null;

/**
 * Perfil de verdade do usuário, ignorando qualquer emulação.
 *
 * Usado para decidir quem pode emular: só quem é master de fato vê os seletores, e
 * quem está emulando precisa continuar vendo-os para conseguir voltar.
 */
export const lePerfilRealDoUsuario = (): string | null => leUsuarioDaSessao()?.permission ?? null;

/**
 * Apaga o espelho local do usuário.
 *
 * Limpa os dois armazenamentos: versões anteriores gravavam no sessionStorage
 * quando o "manter conectado" estava desmarcado.
 */
export const limpaUsuarioDaSessao = (): void => {
  localStorage.removeItem(CHAVE);
  sessionStorage.removeItem(CHAVE);
  localStorage.removeItem(CHAVE_EMULACAO);
};
